import React, { useMemo, useRef, useState } from "react";
import SEO from "../../../components/SEO";
import { Link } from "react-router-dom";
import img72 from "../../../assets/img72.webp";
import { CheckCircle, Plus, Minus } from "lucide-react";

const ContactUs = () => {
  const formRef = useRef(null);

  const [submitted, setSubmitted] = useState(false);
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const BACKEND_BASE =
    import.meta.env.VITE_BACKEND_URL || "https://backend.einsured.ca";

  // null | "loading" | "success" | "error"
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [touched, setTouched] = useState({
    name: false,
    phone: false,
    email: false,
    message: false,
  });

  const MAX_NAME_LEN = 32;

  const normalizeCanadianPhone = (phone) => {
    let digits = String(phone || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1"))
      digits = digits.slice(1);
    // NANP: area code + exchange code cannot start with 0/1
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return "";
    return digits;
  };

  const formatPhoneForDisplay = (digits10) => {
    if (!digits10 || digits10.length !== 10) return "";
    return `+1 (${digits10.slice(0, 3)}) ${digits10.slice(3, 6)}-${digits10.slice(6)}`;
  };

  const isEmailValid = (email) => {
    const v = String(email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
  };

  const isCanadianPhoneValid = (phone) => {
    return !!normalizeCanadianPhone(phone);
  };

  const errors = useMemo(() => {
    const e = {};

    const nm = String(formData.name || "").trim();
    if (!nm) {
      e.name = "Veuillez entrer votre nom.";
    } else if (nm.length > MAX_NAME_LEN) {
      e.name = `Le nom doit comporter ${MAX_NAME_LEN} caractères ou moins.`;
    }

    const ph = String(formData.phone || "").trim();
    if (!ph) {
      e.phone = "Veuillez entrer votre numéro de téléphone.";
    } else if (!isCanadianPhoneValid(ph)) {
      e.phone = "Veuillez entrer un numéro de téléphone canadien valide.";
    }

    const em = String(formData.email || "").trim();
    if (!em) {
      e.email = "Veuillez entrer votre adresse courriel.";
    } else if (!isEmailValid(em)) {
      e.email = "Veuillez entrer une adresse courriel valide.";
    }

    return e;
  }, [formData.name, formData.phone, formData.email]);

  const hasAnyError = Object.keys(errors).length > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;

    let next = value;

    if (name === "name") {
      next = String(value || "").slice(0, MAX_NAME_LEN);
    }

    // optional: prevent letters in phone input
    if (name === "phone") {
      next = String(value || "").replace(/[^0-9+\-\s().]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: next }));

    if (submitted) setSubmitted(false);
    if (status) setStatus(null);
    if (errorMsg) setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = formRef.current;
    if (!form) return;

    setTouched({ name: true, phone: true, email: true, message: true });

    if (hasAnyError) {
      setStatus("error");
      setErrorMsg(
        "Un ou plusieurs champs contiennent une erreur. Veuillez vérifier et réessayer.",
      );

      const firstInvalid = document.querySelector("[aria-invalid='true']");
      if (firstInvalid && typeof firstInvalid.scrollIntoView === "function") {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        try {
          firstInvalid.focus({ preventScroll: true });
        } catch {}
      }

      setSubmitted(false);
      return;
    }
    // ----- End added -----

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    try {
      setStatus("loading");
      setErrorMsg("");
      setSubmitted(false);

      const fd = new FormData();
      fd.append("your_name", (formData.name || "").trim());
      fd.append("your-phone", (formData.phone || "").trim());
      fd.append("your_email", (formData.email || "").trim());
      fd.append("your-message", (formData.message || "").trim());

      const res = await fetch(`${BACKEND_BASE}/api/addContactLead`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: fd,
      });

      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch {
        // ignore
      }

      if (!res.ok) {
        throw new Error(
          json?.message || text || `Request failed (${res.status})`,
        );
      }

      setStatus("success");
      setSubmitted(true);

      setFormData({ name: "", phone: "", email: "", message: "" });

      setTouched({ name: false, phone: false, email: false, message: false });
    } catch (error) {
      setStatus("error");
      setErrorMsg(error?.message || "Une erreur s'est produite. Veuillez réessayer.");
      setSubmitted(false);
    }
  };

  const faqs = [
    {
      question: "Comment puis-je obtenir un devis ?",
      answer:
        "Vous pouvez demander un devis en remplissant notre formulaire en ligne, en nous appelant, en fournissant vos informations à notre créateur de devis en direct, ou en utilisant notre fonction de clavardage en direct. Nous vous poserons quelques questions pour comprendre vos besoins, que ce soit pour une assurance vie ou une assurance super visa, et vous fournirons un devis personnalisé en quelques minutes.",
    },
    {
      question: "Quels types d'assurance vie proposez-vous ?",
      answer:
        "Nous offrons une variété de plans d'assurance vie au Canada et des plans d'assurance voyage, notamment l'assurance vie temporaire, l'assurance vie entière et l'assurance vie universelle. Nos agents travailleront avec vous pour déterminer la meilleure police adaptée à votre situation.",
    },
    {
      question: "Puis-je mettre à jour ma police après l'avoir souscrite ?",
      answer:
        "Oui ! Si votre situation de vie change, par exemple si vous vous mariez, avez un enfant ou achetez une maison, vous souhaiterez peut-être ajuster votre couverture. Contactez-nous et nous vous aiderons à mettre à jour votre police pour vous assurer qu'elle répond toujours à vos besoins, y compris les ajustements à l'assurance vie ou les options comme le compte d'épargne pour l'achat d'une première propriété (CEAP).",
    },
    {
      question: "Comment puis-je déposer une réclamation ?",
      answer:
        "Déposer une réclamation est simple. Contactez-nous par téléphone ou courriel, et l'un de nos courtiers vous guidera tout au long du processus, que ce soit pour une assurance maladies graves, une assurance invalidité ou toute autre police.",
    },
    {
      question: "Que faire si j'ai besoin d'aide pour comprendre ma police ?",
      answer:
        "Nous sommes toujours là pour vous aider à comprendre les détails de votre couverture d'assurance vie. Si vous êtes confus concernant un aspect de votre couverture ou des produits financiers tels que le REER au Canada, appelez-nous simplement ou envoyez-nous un courriel, et nous vous expliquerons tout étape par étape.",
    },
  ];

  const toggleFaq = (index) => {
    setActiveFaqIndex((prev) => (prev === index ? null : index));
  };

  return (
    <>
      <SEO
        title="Contactez-nous | Devis et soutien – einsured.ca"
        description="Contactez einsured.ca pour des conseils personnalisés, des devis et des solutions d’assurance vie, auto, voyage et entreprise adaptées à vos besoins."
        keywords="courtier d'assurance contact, devis d'assurance Canada, soutien assurance, parler à un conseiller en assurance"
      />

      {/* Hero Section */}
      <section
        className="relative min-h-[55vh] sm:min-h-[60vh] md:min-h-[70vh] lg:min-h-[75vh] flex items-center sm:items-start justify-center sm:justify-start bg-cover bg-center"
        style={{ backgroundImage: `url(${img72})` }}
      >
        <div className="absolute inset-0 bg-black/45 md:bg-black/55" />

        <div className="relative z-10 w-full max-w-[1380px] mx-auto px-4 sm:px-8 md:px-16 lg:px-2">
          <div className="flex flex-col lg:flex-row items-start justify-between gap-10 lg:gap-14">
            {/* LEFT: Heading */}
            <div className="w-full lg:w-[42%] text-center sm:text-left mt-[10vh] sm:mt-[12vh] md:mt-[22vh]">
              <h1 className="text-white font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[4rem] leading-tight">
                Nous contacter
              </h1>
            </div>

            {/* RIGHT: Form */}
            <div className="w-full lg:w-[52%] lg:max-w-[560px] mt-6 sm:mt-8 lg:mt-[4vh] lg:ml-auto mb-10 sm:mb-12">
              <div className="bg-white/95 backdrop-blur rounded-3xl shadow-lg border border-white/30 px-5 sm:px-6 lg:px-7 py-6 sm:py-7">
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="space-y-4 sm:space-y-4"
                  noValidate
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Votre nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Votre nom"
                      maxLength={32}
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                      aria-invalid={touched.name && !!errors.name}
                      aria-describedby="contact_name_error"
                      className={
                        "w-full rounded-md border px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 " +
                        (touched.name && errors.name
                          ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                          : "border-gray-300")
                      }
                    />
                    {touched.name && errors.name ? (
                      <p
                        id="contact_name_error"
                        className="mt-2 text-red-600 text-sm"
                      >
                        {errors.name}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Votre téléphone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="Votre téléphone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => {
                        setTouched((t) => ({ ...t, phone: true }));

                        const digits = normalizeCanadianPhone(formData.phone);
                        if (digits) {
                          setFormData((prev) => ({
                            ...prev,
                            phone: formatPhoneForDisplay(digits),
                          }));
                        }
                      }}
                      aria-invalid={touched.phone && !!errors.phone}
                      aria-describedby="contact_phone_error"
                      className={
                        "w-full rounded-md border px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 " +
                        (touched.phone && errors.phone
                          ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                          : "border-gray-300")
                      }
                    />
                    {touched.phone && errors.phone ? (
                      <p
                        id="contact_phone_error"
                        className="mt-2 text-red-600 text-sm"
                      >
                        {errors.phone}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Votre courriel <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="Votre courriel"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                      aria-invalid={touched.email && !!errors.email}
                      aria-describedby="contact_email_error"
                      className={
                        "w-full rounded-md border px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 " +
                        (touched.email && errors.email
                          ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                          : "border-gray-300")
                      }
                    />
                    {touched.email && errors.email ? (
                      <p
                        id="contact_email_error"
                        className="mt-2 text-red-600 text-sm"
                      >
                        {errors.email}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      Votre message
                    </label>
                    <textarea
                      rows={4}
                      name="message"
                      placeholder="Tapez votre message..."
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, message: true }))
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"
                    />
                  </div>

                  <div className="pt-1">
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex items-center justify-center rounded-full bg-[#00b4f0] px-9 sm:px-10 py-2.5 text-sm font-semibold tracking-[0.08em] text-white hover:bg-[#00a0d4] transition-colors w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "loading" ? "ENVOI EN COURS..." : "ENVOYER"}
                    </button>
                  </div>

                  {submitted && status === "success" && (
                    <p className="mt-4 border border-green-500 text-green-700 px-4 py-2 text-sm">
                      Merci pour votre message. Il a bien été envoyé.
                    </p>
                  )}

                  {status === "error" && (
                    <p className="mt-4 border border-red-500 text-red-700 px-4 py-2 text-sm">
                      {errorMsg}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-4">
            Pourquoi choisir einsured.ca ?
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Quand il s'agit de protéger l'avenir de votre famille, vous avez besoin d'un
            assureur en qui vous pouvez avoir confiance. Chez einsured.ca, nous sommes fiers
            de notre engagement envers la satisfaction de nos clients, la transparence et les
            conseils d'experts. Voici ce qui nous distingue :
          </p>
        </div>

        <div className="max-w-[1380px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {[
            {
              title: "Expertise",
              text: "Nos agents agréés ont plus de 12 ans d'expérience dans le secteur de l'assurance vie, se tenant au courant des dernières tendances et réglementations.",
            },
            {
              title: "Service personnalisé",
              text: "Nous ne croyons pas à l'approche universelle. Nous prenons le temps de comprendre vos besoins spécifiques et d'adapter nos recommandations.",
            },
            {
              title: "Tarifs compétitifs",
              text: "Nous travaillons avec des fournisseurs de premier plan pour vous offrir les meilleures options d'assurance vie à des tarifs abordables.",
              highlight: "meilleures options d'assurance vie",
            },
            {
              title: "Approche centrée sur le client",
              text: "Vous n'êtes pas simplement un numéro de police. Vous faites partie de notre famille, et nous nous engageons à vous fournir le meilleur service possible.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 sm:gap-4 bg-[#f5f7fb] rounded-[24px] shadow-md border border-gray-200 px-6 sm:px-8 py-5 sm:py-6"
            >
              <CheckCircle
                className="h-6 w-6 text-sky-500 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                <span className="font-bold text-[#49628b]">{item.title} :</span>{" "}
                {item.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Info & Map */}
      <section
        id="contact-form"
        className="py-16 sm:py-2 px-4 sm:px-6 md:px-24"
      >
        <div className="max-w-[1380px] mx-auto bg-white rounded-3xl shadow-lg border border-gray-200 px-6 sm:px-8 lg:px-10 py-8 sm:py-10">
          <div className="flex flex-col md:flex-row gap-10 lg:gap-14 items-stretch">
            <div className="md:w-1/2 space-y-4 sm:space-y-5">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-[#49628b]">
                  Téléphone
                </h3>
                <a
                  href="tel:18444400128"
                  className="text-base sm:text-lg text-gray-800 hover:text-sky-500"
                >
                  (844) 440-0128
                </a>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-[#49628b]">
                  Courriel
                </h3>
                <a
                  href="mailto:info@einsured.ca"
                  className="text-base sm:text-lg text-gray-800 hover:text-sky-500 break-all"
                >
                  info@einsured.ca
                </a>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-[#49628b]">
                  Adresse
                </h3>
                <a
                  href="https://maps.google.com/?q=9300+Goreway+Drive+Brampton"
                  target="_blank"
                  rel="noreferrer"
                  className="text-base sm:text-lg text-gray-800 hover:text-sky-500"
                >
                  9300 Goreway Drive, #106
                  <br />
                  Brampton, ON L6P 4N1
                </a>
              </div>
            </div>

            <div className="md:w-1/2">
              <div className="w-full h-56 sm:h-64 md:h-72 lg:h-[320px] overflow-hidden rounded-lg border border-gray-200">
                <iframe
                  title="Emplacement einsured.ca"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2881.944208221546!2d-79.682664!3d43.753308!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b3df53e8a4a75%3A0x7d6f5f3e4e4e4e4e!2s9300%20Goreway%20Dr%2C%20Brampton%2C%20ON%20L6P%204N1!5e0!3m2!1sfr!2sca!4v1700000000000!5m2!1sfr!2sca"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Contact Us Grid */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24">
        <div className="max-w-[1380px] mx-auto text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-4">
            Comment nous contacter ?
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Nous offrons plusieurs façons de nous joindre, afin que vous puissiez choisir la méthode
            qui vous convient le mieux.
          </p>
        </div>

        {/* Row 1: Phone + Email */}
        <div className="max-w-[1380px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-10">
          <div className="bg-[#f5f7fb] rounded-[24px] shadow-md border border-gray-200 px-6 sm:px-8 py-6 sm:py-8">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4 sm:mb-5">
              Téléphone
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
              Si vous préférez parler directement à quelqu'un, n'hésitez pas à nous appeler. Nos agents d'assurance agréés sont disponibles pour répondre à vos questions, fournir des devis et vous aider à comprendre votre{" "}
              <span className="text-[#20a7df] font-semibold">
                couverture d'assurance vie
              </span>{" "}
              et vos options financières.
            </p>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6 italic">
              Heures de bureau : du lundi au vendredi, de 10 h à 17 h (HNE)
            </p>
            <div className="flex justify-center">
              <a
                href="tel:+14168400128"
                className="inline-flex items-center justify-center rounded-full bg-[#00b4f0] px-8 sm:px-10 py-3 text-sm sm:text-base font-semibold text-white hover:bg-[#00a0d4] transition-colors w-full sm:w-auto"
              >
                +1 (416) 840-0128
              </a>
            </div>
          </div>

          <div className="bg-[#f5f7fb] rounded-[24px] shadow-md border border-gray-200 px-6 sm:px-8 py-6 sm:py-8">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4 sm:mb-5">
              Courriel
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
              Si vous avez une question plus détaillée ou préférez communiquer par
              courriel, vous pouvez nous contacter à{" "}
              <span className="text-[#20a7df] font-semibold">
                info@einsured.ca
              </span>
              . Que vous ayez besoin d'un devis détaillé sur les{" "}
              <span className="text-[#20a7df] font-semibold">
                plans d'assurance vie au Canada
              </span>{" "}
              ou que vous souhaitiez simplement planifier un rendez-vous, notre support par courriel est
              fiable et efficace.
            </p>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6 italic">
              Heures de bureau : du lundi au vendredi, de 10 h à 17 h (HNE)
            </p>
            <div className="flex justify-center">
              <a
                href="mailto:info@einsured.ca"
                className="inline-flex items-center justify-center rounded-full bg-[#00b4f0] px-8 sm:px-10 py-3 text-sm sm:text-base font-semibold text-white hover:bg-[#00a0d4] transition-colors w-full sm:w-auto"
              >
                info@einsured.ca
              </a>
            </div>
          </div>
        </div>

        {/* Row 2: Online Contact Form (left) + Visit Us (right) */}
        <div className="max-w-[1380px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          <div className="bg-[#f5f7fb] rounded-[24px] shadow-md border border-gray-200 px-6 sm:px-8 py-6 sm:py-8">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4 sm:mb-5">
              Prendre un rendez-vous
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
              Si vous préférez, vous pouvez prendre un bref rendez-vous sans obligation en cliquant sur le lien ci-dessous. Sélectionnez un créneau disponible et entrez vos informations de base. Un conseiller vous contactera à l'heure prévue.
            </p>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6 italic">
              Heures de bureau : du lundi au vendredi, de 10 h à 17 h (HNE)
            </p>
            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-[#00b4f0] px-8 sm:px-10 py-3 text-sm sm:text-base font-semibold text-white hover:bg-[#00a0d4] transition-colors w-full sm:w-auto"
              >
                Prendre un rendez-vous
              </a>
            </div>
          </div>

          <div className="bg-[#f5f7fb] rounded-[24px] shadow-md border border-gray-200 px-6 sm:px-8 py-6 sm:py-8">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4 sm:mb-5">
              Nous rendre visite en personne
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
              Si vous préférez nous rencontrer en personne, nous vous accueillons à
              notre bureau situé à Brampton. Notre personnel accueillant sera
              heureux de vous aider pour toute demande, notamment pour en savoir plus
              sur le{" "}
              <Link
                to="/fr/regime-epargne-etudes"
                className="text-[#20a7df] font-semibold hover:underline"
              >
                Régime enregistré d'épargne-études
              </Link>
              , la couverture d'assurance vie, ou{" "}
              <span className="text-[#20a7df] font-semibold">
                l'assurance maladies graves
              </span>
              .
            </p>

            <div className="mb-6">
              <p className="text-sm sm:text-base font-semibold text-gray-800">
                Adresse :
              </p>
              <p className="text-sm sm:text-base text-gray-700">
                9300 Goreway Drive, Unité 106
                <br />
                Brampton, ON L6P 4N1
              </p>
            </div>

            <div className="flex justify-center">
              <a
                href="https://maps.google.com/?q=9300+Goreway+Drive+Brampton"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-[#00b4f0] px-8 sm:px-10 py-3 text-sm sm:text-base font-semibold text-white hover:bg-[#00a0d4] transition-colors w-full sm:w-auto"
              >
                Obtenir l'itinéraire
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto text-center mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
            Questions fréquemment posées
          </h2>
        </div>

        <div className="max-w-[1380px] mx-auto space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => {
            const expanded = activeFaqIndex === index;
            const panelId = `contact-faq-panel-${index}`;
            const btnId = `contact-faq-btn-${index}`;

            return (
              <div
                key={index}
                className="border border-gray-200 rounded-md bg-white shadow-sm"
              >
                <button
                  id={btnId}
                  type="button"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  className="w-full flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 text-left text-sm sm:text-base font-semibold text-[#49628b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2b4f7c]"
                >
                  <span className="pr-4">{faq.question}</span>
                  {expanded ? (
                    <Minus className="w-5 h-5 text-[#49628b] flex-shrink-0" />
                  ) : (
                    <Plus className="w-5 h-5 text-[#49628b] flex-shrink-0" />
                  )}
                </button>

                {expanded && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={btnId}
                    className="px-4 sm:px-6 pb-4 text-sm sm:text-base text-gray-700 leading-relaxed bg-[#f5f7fb]"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default ContactUs;
